import {create} from 'zustand';
import { ItemGroup, ItemCategory, uomDetails } from '../Common/inventory.helper';
import { IndentListResponse } from '../StockTransfer/stockTransfer.helper';



interface StockTransferStore {

    isAddIndentOpen: boolean;
    setIsAddIndentOpen: (isOpen: boolean) => void;
  isStockDetailsOpen: boolean;
  setIsStockDetailsOpen: (isOpen: boolean) => void;
  isAddIssueOpen: boolean;
  setIsAddIssueOpen: (isOpen: boolean) => void;
    selectedIndentIds: string[];
    setSelectedIndentIds: (ids: string[]) => void;
  selectedIndentRows: IndentListResponse[];
  setSelectedIndentRows: (rows: IndentListResponse[]) => void;
  editIndentId?: number | null;
  setEditIndentId?: (id: number | null) => void;

  // Filter selections
  fromClinicId: number | null;
  setFromClinicId: (id: number | null) => void;
  fromStoreId: number | null;
  setFromStoreId: (id: number | null) => void;
  toClinicId: number | null;
  setToClinicId: (id: number | null) => void;
  toStoreId: number | null;
  setToStoreId: (id: number | null) => void;

  // Indent date range (YYYY-MM-DD strings)
  fromDate: string | null;
  setFromDate: (date: string | null) => void;
  toDate: string | null;
  setToDate: (date: string | null) => void;

  // Apply trigger to notify parent to fetch list
  applyFiltersTrigger: number;
  triggerApplyFilters: () => void;

  // Item filters for Add Indent
  addIndentFilterValues: {
    ItemGroupList: ItemGroup[];
    ItemCategoryList: ItemCategory[];
  };
  setAddIndentFilterValues: (values: { ItemGroupList: ItemGroup[]; ItemCategoryList: ItemCategory[] }) => void;

  // Issued list total count
  issuedTotalCount: number;
  setIssuedTotalCount: (count: number) => void;

  // Prospective list total count
  prospectiveTotalCount: number;
  setProspectiveTotalCount: (count: number) => void;

  // Search text for Issue/Prospective lists
  searchText: string;
  setSearchText: (text: string) => void;

  // Top tab selection for Issue page
  issueTopTab: 'PROSPECTIVE' | 'ISSUED';
  setIssueTopTab: (tab: 'PROSPECTIVE' | 'ISSUED') => void;

  // Top tab selection for Receive page
  receiveTopTab: 'PROSPECTIVE' | 'RECEIVED';
  setReceiveTopTab: (tab: 'PROSPECTIVE' | 'RECEIVED') => void;

  // Selected Issue Items (Indent Item List selections)
  selectedIssueItems: IssueSelectedItem[];
  setSelectedIssueItems: (items: IssueSelectedItem[]) => void;
  addSelectedIssueItems: (items: IssueSelectedItem[]) => void;
  removeSelectedIssueItemsByIndentDetailIds: (ids: number[]) => void;
  removeSelectedIssueItemByIndentDetailId: (id: number) => void;
  removeSelectedIssueItemByIndentDetailIdAndBatch: (id: number, batch: number | null) => void;
  clearSelectedIssueItems: () => void;

  // Selected Receive Items (Receive Item List selections)
  selectedReceiveItems: ReceiveSelectedItem[];
  setSelectedReceiveItems: (items: ReceiveSelectedItem[]) => void;
  addSelectedReceiveItems: (items: ReceiveSelectedItem[]) => void;
  removeSelectedReceiveItemsByIssueDetailIds: (ids: number[]) => void;
  removeSelectedReceiveItemByIssueDetailId: (id: number) => void;
  clearSelectedReceiveItems: () => void;

  // Selected Stock Items for Add Indent flow
  selectedStockItems: SelectedStockItem[];
  setSelectedStockItems: (items: SelectedStockItem[]) => void;
  clearSelectedStockItems: () => void;

  // Store info for Add Indent
  selectedStockClinicId: number | null;
  setSelectedStockClinicId: (id: number | null) => void;
  selectedStockStoreId: number | null;
  setSelectedStockStoreId: (id: number | null) => void;

}

export type IssueSelectedItem = {
  indent_detail_id: number;
  issue_qty: number;
  batch: number | null;
  base_cost_price: number;
  base_mrp: number;
  total_base_cost_price: number;
  total_mrp_cost_price: number;
  uom: string;
};

export type ReceiveSelectedItem = {
  issue_detail_id: number;
  received_qty: number;
  base_mrp: string;
  base_cost_price: string;
  total_base_cost_price: string;
  total_mrp_cost_price: string;
  sgst_percent: number;
  sgst_amount: string;
  cgst_percent: number;
  cgst_amount: string;
};

export type SelectedStockItem = {
  itemId: number;
  itemCode: string;
  itemName: string;
  batchId: number | null;
  batchCode: string;
  available_qty: number;
  base_uom_name?: string;
  base_uom_code?: string;
  clinic_id: number;
  clinic_name: string;
  store_id: number;
  store_name: string;
  uom_conversions?: uomDetails[];
  max_qty?: number;
};

export const useStockTransferStore = create<StockTransferStore>((set) => ({

  isAddIndentOpen: false,
  setIsAddIndentOpen: (isOpen) => set(() => ({ isAddIndentOpen: isOpen })),
  isStockDetailsOpen: false,
  setIsStockDetailsOpen: (isOpen) => set(() => ({ isStockDetailsOpen: isOpen })),
  isAddIssueOpen: false,
  setIsAddIssueOpen: (isOpen) => set(() => ({ isAddIssueOpen: isOpen })),
  selectedIndentIds: [],
  setSelectedIndentIds: (ids) => set(() => ({ selectedIndentIds: ids })),
  selectedIndentRows: [],
  setSelectedIndentRows: (rows) => set(() => ({ selectedIndentRows: rows })),
  editIndentId: null,
  setEditIndentId: (id) => set(() => ({ editIndentId: id })),

  // Filter selections
  fromClinicId: null,
  setFromClinicId: (id) => set(() => ({ fromClinicId: id })),
  fromStoreId: null,
  setFromStoreId: (id) => set(() => ({ fromStoreId: id })),
  toClinicId: null,
  setToClinicId: (id) => set(() => ({ toClinicId: id })),
  toStoreId: null,
  setToStoreId: (id) => set(() => ({ toStoreId: id })),

  // Indent date range
  fromDate: null,
  setFromDate: (date) => set(() => ({ fromDate: date })),
  toDate: null,
  setToDate: (date) => set(() => ({ toDate: date })),

  // Apply trigger
  applyFiltersTrigger: 0,
  triggerApplyFilters: () => set((state) => ({ applyFiltersTrigger: state.applyFiltersTrigger + 1 })),

  // Item filters for Add Indent
  addIndentFilterValues: {
    ItemGroupList: [],
    ItemCategoryList: [],
  },
  setAddIndentFilterValues: (values) => set(() => ({ addIndentFilterValues: values })),

  // Issued list total count
  issuedTotalCount: 0,
  setIssuedTotalCount: (count) => set(() => ({ issuedTotalCount: count })),

  // Prospective list total count
  prospectiveTotalCount: 0,
  setProspectiveTotalCount: (count) => set(() => ({ prospectiveTotalCount: count })),

  // Search text
  searchText: '',
  setSearchText: (text) => set(() => ({ searchText: text })),

  // Top tab state for Issue page
  issueTopTab: 'PROSPECTIVE',
  setIssueTopTab: (tab) => set(() => ({ issueTopTab: tab })),

  // Top tab state for Receive page
  receiveTopTab: 'PROSPECTIVE',
  setReceiveTopTab: (tab) => set(() => ({ receiveTopTab: tab })),

  // Selected Issue Items state
  selectedIssueItems: [],
  setSelectedIssueItems: (items) => set(() => ({ selectedIssueItems: items })),
  addSelectedIssueItems: (items) => set((state) => ({ selectedIssueItems: [...state.selectedIssueItems, ...items] })),
  removeSelectedIssueItemsByIndentDetailIds: (ids) => set((state) => ({ selectedIssueItems: state.selectedIssueItems.filter((it) => !ids.includes(it.indent_detail_id)) })),
  removeSelectedIssueItemByIndentDetailId: (id) => set((state) => {
    const idx = state.selectedIssueItems.findIndex((it) => it.indent_detail_id === id);
    if (idx === -1) return state;
    const next = state.selectedIssueItems.slice();
    next.splice(idx, 1);
    return { selectedIssueItems: next };
  }),
  removeSelectedIssueItemByIndentDetailIdAndBatch: (id, batch) => set((state) => {
    const idx = state.selectedIssueItems.findIndex((it) => it.indent_detail_id === id && it.batch === batch);
    if (idx === -1) return state;
    const next = state.selectedIssueItems.slice();
    next.splice(idx, 1);
    return { selectedIssueItems: next };
  }),
  clearSelectedIssueItems: () => set(() => ({ selectedIssueItems: [] })),

  // Selected Receive Items state
  selectedReceiveItems: [],
  setSelectedReceiveItems: (items) => set(() => ({ selectedReceiveItems: items })),
  addSelectedReceiveItems: (items) => set((state) => ({ selectedReceiveItems: [...state.selectedReceiveItems, ...items] })),
  removeSelectedReceiveItemsByIssueDetailIds: (ids) => set((state) => ({ selectedReceiveItems: state.selectedReceiveItems.filter((it) => !ids.includes(it.issue_detail_id)) })),
  removeSelectedReceiveItemByIssueDetailId: (id) => set((state) => {
    const idx = state.selectedReceiveItems.findIndex((it) => it.issue_detail_id === id);
    if (idx === -1) return state;
    const next = state.selectedReceiveItems.slice();
    next.splice(idx, 1);
    return { selectedReceiveItems: next };
  }),
  clearSelectedReceiveItems: () => set(() => ({ selectedReceiveItems: [] })),

  // Selected Stock Items state
  selectedStockItems: [],
  setSelectedStockItems: (items) => set(() => ({ selectedStockItems: items })),
  clearSelectedStockItems: () => set(() => ({ selectedStockItems: [] })),

  // Store info for Add Indent
  selectedStockClinicId: null,
  setSelectedStockClinicId: (id) => set(() => ({ selectedStockClinicId: id })),
  selectedStockStoreId: null,
  setSelectedStockStoreId: (id) => set(() => ({ selectedStockStoreId: id })),
}));